export default `:root {
  --black: #0b0c0c;
  --blue: #1d70b8;
  --dark-blue: #003078;
  --green: #00703c;
  --light-grey: #f3f2f1;
  --mid-grey: #b1b4b6;
  --white: #ffffff;
  --yellow: #ffdd00;
  color: var(--black);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 18px;
  line-height: 1.5;
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  background: var(--white);
}

a {
  color: var(--blue);
  text-decoration-thickness: 2px;
  text-underline-offset: 0.15em;
}

a:hover {
  color: var(--dark-blue);
  text-decoration-thickness: 3px;
}

a:focus,
.button:focus {
  color: var(--black);
  background: var(--yellow);
  box-shadow: 0 -2px var(--yellow), 0 4px var(--black);
  outline: 3px solid transparent;
  text-decoration: none;
}

.page-width {
  width: min(1180px, calc(100% - 40px));
  margin-inline: auto;
}

.skip-link {
  position: absolute;
  z-index: 20;
  top: -80px;
  left: 16px;
  padding: 10px 15px;
  color: var(--black);
  background: var(--yellow);
  font-weight: 700;
}

.skip-link:focus {
  top: 10px;
}

.site-header {
  border-bottom: 10px solid var(--blue);
  color: var(--white);
  background: var(--black);
}

.header-inner {
  display: flex;
  min-height: 74px;
  padding-block: 16px;
  gap: 24px;
  align-items: baseline;
}

.govuk-wordmark {
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: -0.04em;
}

.service-name {
  font-size: 1.1rem;
  font-weight: 700;
}

.service-bar {
  padding-block: 11px;
  border-bottom: 1px solid var(--mid-grey);
  font-weight: 700;
}

.intro {
  max-width: 820px;
  padding-block: 54px 48px;
}

.eyebrow,
h1,
h2,
h3,
p {
  margin-top: 0;
}

.eyebrow {
  margin-bottom: 4px;
  color: var(--blue);
  font-weight: 700;
}

h1 {
  max-width: 720px;
  margin-bottom: 18px;
  font-size: clamp(2.35rem, 6vw, 4.3rem);
  line-height: 1.02;
  letter-spacing: -0.045em;
}

h2 {
  margin-bottom: 20px;
  font-size: clamp(1.65rem, 3vw, 2.25rem);
  line-height: 1.15;
  letter-spacing: -0.025em;
}

h3 {
  margin-bottom: 8px;
  font-size: 1rem;
}

.lead {
  max-width: 700px;
  margin-bottom: 28px;
  font-size: 1.25rem;
}

.button {
  display: inline-block;
  padding: 11px 19px 9px;
  color: var(--white);
  background: var(--green);
  box-shadow: 0 3px 0 #002d18;
  font-weight: 700;
  text-decoration: none;
}

.button:hover {
  color: var(--white);
  background: #005a30;
}

.image-note {
  margin-top: 22px;
  color: #505a5f;
  font-size: 0.9rem;
}

.guide-layout {
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: 56px;
  align-items: start;
}

.contents {
  position: sticky;
  top: 20px;
  max-height: calc(100vh - 40px);
  margin-bottom: 40px;
  padding: 20px 20px 18px;
  overflow: auto;
  border-left: 5px solid var(--blue);
  background: var(--light-grey);
}

.contents h2 {
  margin-bottom: 16px;
  font-size: 1.15rem;
}

.contents h3 {
  margin-top: 20px;
}

.contents ol {
  margin: 0;
  padding-left: 22px;
  font-size: 0.85rem;
}

.contents li + li {
  margin-top: 6px;
}

.guide {
  min-width: 0;
}

.task {
  display: grid;
  grid-template-columns: minmax(260px, 0.8fr) minmax(360px, 1.2fr);
  padding: 10px 0 72px;
  gap: 38px;
  align-items: start;
  scroll-margin-top: 24px;
}

.task + .task {
  padding-top: 62px;
  border-top: 1px solid var(--mid-grey);
}

.task-wide,
.task-text-only {
  grid-template-columns: 1fr;
}

.task-wide .task-copy,
.task-text-only .task-copy {
  max-width: 760px;
}

.task-copy ol,
.task-copy ul {
  margin: 0 0 20px;
  padding-left: 28px;
}

.task-copy li {
  padding-left: 5px;
}

.task-copy li + li {
  margin-top: 8px;
}

.step-number {
  display: grid;
  width: 40px;
  height: 40px;
  margin-bottom: 14px;
  border-radius: 50%;
  place-items: center;
  color: var(--white);
  background: var(--blue);
  font-weight: 800;
}

.important {
  padding: 12px 0 12px 16px;
  border-left: 5px solid var(--blue);
  font-weight: 700;
}

.save-notes {
  margin-block: 22px;
  padding: 16px 18px 4px;
  background: var(--light-grey);
}

.save-notes p {
  margin-bottom: 12px;
}

.screenshot {
  min-width: 0;
  margin: 0;
}

.screenshot img {
  display: block;
  width: 100%;
  height: auto;
  border: 1px solid var(--mid-grey);
  background: var(--white);
}

.screenshot figcaption {
  margin-top: 8px;
  color: #505a5f;
  font-size: 0.85rem;
}

.screenshot-tall img {
  max-height: 780px;
  object-fit: contain;
  object-position: top left;
}

.screenshot-full {
  width: 100%;
}

.screenshot-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.screenshot-stack {
  display: grid;
  gap: 34px;
}

.site-footer {
  margin-top: 22px;
  padding-block: 42px;
  border-top: 10px solid var(--blue);
  background: var(--light-grey);
}

.site-footer p {
  margin-bottom: 6px;
  font-weight: 700;
}

@media (max-width: 940px) {
  .guide-layout {
    display: block;
  }

  .contents {
    position: static;
    max-height: none;
  }

  .task {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .screenshot {
    width: min(100%, 760px);
  }
}

@media (max-width: 560px) {
  :root {
    font-size: 16px;
  }

  .page-width {
    width: min(100% - 28px, 1180px);
  }

  .header-inner {
    display: block;
  }

  .service-name {
    display: block;
    margin-top: 4px;
  }

  .intro {
    padding-block: 40px;
  }

  .task,
  .task + .task {
    padding-block: 46px;
  }

  .screenshot-pair {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
}

@media print {
  .site-header,
  .service-bar,
  .contents,
  .button,
  .site-footer {
    display: none;
  }

  .task {
    break-inside: avoid;
    grid-template-columns: 1fr;
    padding-block: 24px;
  }
}
`;
